import {
  Divider,
  EHeadingStyleVariant,
  EModalHeaderTextAlignVariant,
  EModalSize,
  ETextStyleVariant,
  GeneralInstantBookIcon,
  Heading,
  Modal,
  Text,
} from '@outdoorsyco/bonfire';
import React, { Fragment, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { useBreakpointContext } from '@/components/common/BreakpointContext';
import { defaultLocale } from '@/config/locales';
import { getSearchViewedEventData } from '@/redux/selectors/analytics-selectors';
import { calcDiscount } from '@/redux/selectors/listing/bill';
import { PriceType } from '@/redux/selectors/listing/page';
import { trackPriceBreakdownPreviewedEvent } from '@/services/analytics/listings';
import { formatCurrency } from '@/utility/currency';
import { isAutoRentalTile, TAutoRentalTile } from '@/utility/mapAutoSearchResultToTile';
import { IRentalTile } from '@/utility/mapSearchResultToTile';

type TListingTileTotalPriceProps = {
  listingTile: IRentalTile | TAutoRentalTile;
  className?: string;
};

export const ListingTileTotalPrice = ({
  listingTile,
  className = '',
}: TListingTileTotalPriceProps) => {
  const {
    id: listingId,
    instantBook = false,
    currency = defaultLocale.base_currency,
    price,
  } = listingTile;

  const intl = useIntl();

  const { isMobile } = useBreakpointContext();
  const [showBreakdown, setShowBreakdown] = useState(false);

  const searchEventData = useSelector(getSearchViewedEventData);

  if (!price?.estimatedPriceBreakdown) return null;

  const formattedTotalEstimatedPrice = formatCurrency({
    currency,
    digits: 2,
    priceInCents: price.estimatedPriceBreakdown.total,
  });

  const breakdown = [
    {
      label: intl.formatMessage(
        {
          defaultMessage:
            '{pricePerNight} × {numberOfNights} {period, select, day {days} night {nights} other {}}',
          id: 'PDJ0x7',
        },
        {
          pricePerNight: formatCurrency({
            currency,
            digits: 2,
            priceInCents: price?.currentPrice || 0,
          }),
          numberOfNights: price.estimatedPriceBreakdown.duration,
          period: price?.period || PriceType.NIGHT,
        },
      ),
      amount: (price?.currentPrice || 0) * price.estimatedPriceBreakdown.duration,
    },
    price.estimatedPriceBreakdown.insurance > 0
      ? {
          label: intl.formatMessage({ defaultMessage: 'Insurance', id: 'qK9GW3' }),
          amount: price.estimatedPriceBreakdown.insurance,
        }
      : undefined,
    price.estimatedPriceBreakdown.prep_fee > 0
      ? {
          label: intl.formatMessage({ defaultMessage: 'Prep fee', id: 'LOKQfC' }),
          amount: price.estimatedPriceBreakdown.prep_fee,
        }
      : undefined,
    price.estimatedPriceBreakdown.guest_fee > 0
      ? {
          label: intl.formatMessage({ defaultMessage: 'Service fee', id: 'G0bXT7' }),
          amount: price.estimatedPriceBreakdown.guest_fee,
        }
      : undefined,
    price.estimatedPriceBreakdown.delivery_fee > 0
      ? {
          label: intl.formatMessage({ defaultMessage: 'Delivery fee', id: 'jCB9UK' }),
          amount: price.estimatedPriceBreakdown.delivery_fee,
        }
      : undefined,
  ].filter(Boolean) as { label: string; amount: number }[];

  const breakdownTotalPrice = breakdown.reduce((acc, { amount }) => acc + amount, 0);

  const showEstimatedBreakdownTitle = intl.formatMessage({
    description: '[Price]: screen reader message for estimated total pre-tax breakdown',
    defaultMessage: 'Estimated total pre-tax breakdown',
    id: '34Pnmx',
  });

  return (
    <div className={`flex items-center flex-nowrap gap-1 ${className}`}>
      {instantBook && (
        <GeneralInstantBookIcon
          aria-label={intl.formatMessage({
            description: '[Price]: screen reader message for instant book',
            defaultMessage: 'This listing allows instant booking.',
            id: 'ZpLmIK',
          })}
          className="w-4 h-4 text-canvas-300 shrink-0"
        />
      )}

      <Text
        component="div"
        variant={ETextStyleVariant.LargeBold}
        aria-label={intl.formatMessage(
          {
            description: '[Price]: screen reader message for total estimated price',
            defaultMessage: '{total} estimated total pre-tax',
            id: 'o3DL1J',
          },
          { total: formattedTotalEstimatedPrice },
        )}>
        {formattedTotalEstimatedPrice}
      </Text>

      <button
        aria-label={showEstimatedBreakdownTitle}
        title={showEstimatedBreakdownTitle}
        // Prevent opening the tile when clicking on the button
        onClick={e => {
          e.preventDefault();
          e.stopPropagation();

          setShowBreakdown(true);

          if (listingId && price.estimatedPriceBreakdown) {
            trackPriceBreakdownPreviewedEvent({
              basePricePerNight: price?.originalPrice || 0,
              currentPageViewed: searchEventData?.currentPageViewed || 1,
              deliveryFee: price.estimatedPriceBreakdown.delivery_fee,
              discountPercentOff: calcDiscount(price?.originalPrice || 0, price?.currentPrice || 0),
              duration: price.estimatedPriceBreakdown.duration,
              serviceFee: price.estimatedPriceBreakdown.guest_fee,
              insurance: price.estimatedPriceBreakdown.insurance,
              isCampground: false,
              isInstabook: instantBook,
              pricePerNight: price?.currentPrice || 0,
              prepFee: price.estimatedPriceBreakdown.prep_fee,
              rentalAmount: price.estimatedPriceBreakdown.rental_amount,
              rentalAmountPerDay: price.estimatedPriceBreakdown.rental_amount_per_day,
              rentalID: listingId,
              rentalType: (() => {
                if (isAutoRentalTile(listingTile)) return 'auto';
                return listingTile.isStay ? 'stay' : 'rv';
              })(),
              total: price.estimatedPriceBreakdown.total,
              useDayPricing: price.estimatedPriceBreakdown.use_day_pricing,
            });
          }
        }}
        className="overflow-hidden">
        <Text variant={ETextStyleVariant.MediumRegular} className="underline truncate text-gray-50">
          {intl.formatMessage({
            defaultMessage: 'estimated total pre-tax',
            id: 'OOx0Kq',
          })}
        </Text>
      </button>

      {showBreakdown && (
        // Prevent clicks from bubbling up to the tile
        // eslint-disable-next-line
        <div onClick={e => e.stopPropagation()} className="hidden">
          <Modal
            isOpen={showBreakdown}
            onClose={() => setShowBreakdown(false)}
            size={isMobile ? EModalSize.Small : EModalSize.Medium}>
            <Modal.Actions />

            <Modal.Header textAlign={EModalHeaderTextAlignVariant.Center}>
              <Heading variant={isMobile ? EHeadingStyleVariant.H4 : EHeadingStyleVariant.H6}>
                <FormattedMessage defaultMessage="Price estimate" id="pUTJFf" />
              </Heading>
            </Modal.Header>

            <Modal.Content>
              <div className="p-2 md:py-6 md:px-4">
                {breakdown.map((item, index) => (
                  <Fragment key={index}>
                    <div className="flex items-center py-2 gap-1 md:py-4">
                      <Text
                        component="div"
                        variant={ETextStyleVariant.MediumRegular}
                        className="flex-grow">
                        {item.label}
                      </Text>

                      <Text
                        component="div"
                        variant={ETextStyleVariant.MediumBold}
                        className="text-right">
                        {formatCurrency({
                          currency,
                          digits: 2,
                          priceInCents: item.amount,
                        })}
                      </Text>
                    </div>

                    <Divider />
                  </Fragment>
                ))}

                <div className="flex items-center py-2 gap-1 md:py-4">
                  <Text
                    component="div"
                    variant={ETextStyleVariant.MediumBold}
                    className="flex-grow">
                    {intl.formatMessage({ defaultMessage: 'Total Price', id: 'mah+Bl' })}
                  </Text>

                  <Text
                    component="div"
                    variant={ETextStyleVariant.MediumBold}
                    className="text-right">
                    {formatCurrency({
                      currency,
                      digits: 2,
                      priceInCents: breakdownTotalPrice,
                    })}
                  </Text>
                </div>
              </div>
            </Modal.Content>
          </Modal>
        </div>
      )}
    </div>
  );
};
